import api from './api';

/**
 * Rentabilité par camion, fret par trajet, actif camion, début d'exercice (lot 019).
 *
 * Contrat: spec 019 (rentabilité et amortissement). Toutes ces routes sont
 * réservées à la direction (montants): un autre rôle reçoit un refus en français que
 * l'écran affiche tel quel (`apiErrorMessage`).
 *
 * Les réponses sont renvoyées telles que le serveur les écrit: aucun cache, aucune
 * file hors ligne — un montant périmé serait pire qu'un montant absent.
 */

const nettoyer = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );

export const economicsAPI = {
  /** Compte d'exploitation d'un camion pour un mois. */
  async getTruckEconomics(truckId, { year, month } = {}) {
    const response = await api.get(`/trucks/${truckId}/economics`, {
      params: nettoyer({ year, month }),
    });
    return response.data;
  },

  /** Rentabilité de toute la flotte pour un mois (lot 020 et tableaux de synthèse). */
  async getFleetEconomics({ year, month, companyId } = {}) {
    const response = await api.get('/fleet/economics', {
      params: nettoyer({ year, month, company_id: companyId }),
    });
    return response.data;
  },

  // --- Actif camion (amortissement) ---------------------------------------
  async getTruckAsset(truckId) {
    const response = await api.get(`/trucks/${truckId}/asset`);
    return response.data;
  },

  /**
   * Crée ou remplace l'actif. `values`: { acquisition_cost, acquisition_date,
   * useful_life_months, residual_value, disposal_date }.
   */
  async saveTruckAsset(truckId, values) {
    const response = await api.put(`/trucks/${truckId}/asset`, values);
    return response.data;
  },

  async deleteTruckAsset(truckId) {
    const response = await api.delete(`/trucks/${truckId}/asset`);
    return response.data;
  },

  // --- Début de l'exercice -------------------------------------------------
  async getFiscalYear() {
    const response = await api.get('/organizations/me/fiscal-year');
    return response.data;
  },

  async saveFiscalYear(startMonth) {
    const response = await api.put('/organizations/me/fiscal-year', {
      start_month: Number(startMonth),
    });
    return response.data;
  },

  // --- Fret d'un trajet ----------------------------------------------------
  async getTripFreight(tripId) {
    const response = await api.get(`/trips/${tripId}/freight`);
    return response.data;
  },

  /** `values`: { mode, basis, rate_per_liter, amount, client_id }. */
  async saveTripFreight(tripId, values) {
    const response = await api.put(`/trips/${tripId}/freight`, values);
    return response.data;
  },

  async clearTripFreight(tripId) {
    const response = await api.delete(`/trips/${tripId}/freight`);
    return response.data;
  },

  /** Génère la facture de transport (une seule; rejouée, rend la même). */
  async createFreightInvoice(tripId, clientId) {
    const response = await api.post(
      `/trips/${tripId}/freight/invoice`,
      clientId ? { client_id: clientId } : {},
    );
    return response.data;
  },

  /** Tarif de fret au litre que le barème propose, ou `null`. */
  async getFreightTariff({ corridorId, destination, clientId } = {}) {
    const response = await api.get('/freight/tariff', {
      params: nettoyer({
        corridor_id: corridorId,
        destination,
        client_id: clientId,
      }),
    });
    return response.data?.tariff || null;
  },
};

/** Type d'opération d'un barème de fret dans `/pricing-rules`. */
export const FREIGHT_OPERATION_TYPE = 'FREIGHT';

export const estTarifFret = (regle) =>
  String(regle?.operation_type || '').trim().toUpperCase() === FREIGHT_OPERATION_TYPE;

/**
 * Barèmes de fret, saisis par `/pricing-rules` (route existante): une règle
 * `operation_type = FREIGHT` avec `price_per_liter` et, dans `conditions_json`,
 * `corridor_id` et/ou `destination`; `client_id` facultatif.
 */
export const freightRulesAPI = {
  async list() {
    const response = await api.get('/pricing-rules');
    const rows = Array.isArray(response.data) ? response.data : response.data?.data || [];
    return rows;
  },

  async create({ name, pricePerLiter, corridorId, destination, clientId, currency = 'XOF' }) {
    const conditions = {};
    if (corridorId) conditions.corridor_id = corridorId;
    if (destination && String(destination).trim()) conditions.destination = String(destination).trim();
    const response = await api.post('/pricing-rules', {
      name: name || null,
      operation_type: FREIGHT_OPERATION_TYPE,
      price_per_liter: Number(pricePerLiter),
      currency,
      client_id: clientId || null,
      conditions_json: conditions,
      is_active: true,
    });
    return response.data;
  },

  async setActive(ruleId, isActive) {
    const response = await api.patch(`/pricing-rules/${ruleId}`, { is_active: Boolean(isActive) });
    return response.data;
  },

  async remove(ruleId) {
    await api.delete(`/pricing-rules/${ruleId}`);
  },
};

export default economicsAPI;
